import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { CHAT_MESSAGES_LOADING } from "../../../../constants/loading.constant";
import { useMeCore } from "../../../../cores/me";
import { Conversation, Message } from "../../../../cores/socialChat/interfaces/conversations";
import { PanelWembii } from "../../PanelWembii";
import { UpdateLoadingCard } from "../../UpdateLoadingCard";
import MessageRow from "../MessageRow";
import SubmitInput from "../SubmitInput";
import { useIntl } from "react-intl";

interface Props {
  selected: Conversation | null;
  messages: Message[];
  getMessages: (arg0: string, arg1: string) => void;
  postMessage: (arg0: string, arg1: string, arg2: string, arg3: string) => void;
  postSeenMessage?: (arg0: string, arg1: string, arg2: string) => void;
}

export default function SocialChatForm(props: Props) {
  const intl = useIntl();
  const {
    selected,
    messages,
    getMessages,
    postMessage,
    postSeenMessage,
  } = props;
  const { company, socialSettings } = useMeCore();

  const ref = useRef<any>(null);

  useEffect(() => {
    if (ref !== null) {
      ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [messages]);

  const meId = useMemo(() => {
    if (selected?.type === 'facebook')
    return socialSettings?.facebook?.userId;
    if (selected?.type === 'instagram')
    return socialSettings?.instagram?.userId;
  }, [selected, socialSettings]);

  const itsMe = useCallback((message: Message) => {
    if (selected?.type === 'whatsapp')
      return !!message.from.email;
    return meId === message.from.id;
  }, [selected, meId]);

  const onSubmit = (text: string) => {
    postMessage(company?.id!, selected?.id!, selected?.participants[0]?.id!, text);
  };

  const disabled = useCallback(() => {
    let isDisabled = true;

    if (selected?.type !== 'whatsapp') {
      const messagesFromUser = messages.filter(
        (message) => message.from.id !== meId
      );
      if (messagesFromUser.length > 0) {
        const lastMessage = messagesFromUser[messagesFromUser.length - 1].timestamp;
        const lastMessageTime = moment(`${lastMessage.date} ${lastMessage.time}`);
        const minutesDiff = moment()
                    .utc()
                    .diff(lastMessageTime, 'minutes');
  
        isDisabled = minutesDiff > (24 * 60);
      }
    } else {
      isDisabled = false;
    }

    return isDisabled;
  }, [messages, meId, selected]);

  useEffect(() => {
    getMessages(company?.id!, selected?.id!);
  }, [getMessages, selected, company]);

  useEffect(() => {
    if (!disabled()) {
      postSeenMessage?.(company?.id!, selected?.id!, selected?.fromId!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, company, postSeenMessage, disabled]);

  return (
    <PanelWembii shadow className="p-3 mb-3 position-relative">
      <UpdateLoadingCard
        className="position-absolute-center"
        loadingId={CHAT_MESSAGES_LOADING}
        text={intl.formatMessage({ id: "app.chatFacebook.updatingMsg" })}
      />
      <div
        className="overflow-auto hide-scrollbar"
        style={{
          height: "calc(100vh - 430px)",
        }}
      >
        {messages.map((message) => (
          <MessageRow
            key={message.id}
            message={message}
            itsMe={itsMe(message)}
          />
        ))}
        <div ref={ref} />
      </div>
      {disabled() && (
        <span className="text-center text-muted">
         {intl.formatMessage({ id: "app.chatFacebook.notHaveMsg" })}
        </span>
      )} 
      <SubmitInput onSubmit={onSubmit} disabled={disabled()} />
    </PanelWembii>
  );
}
